import { EMPLOYEE_ROLE_ID } from "../common/Functions";

export const DEFAULT_BOARD_LIST_NAMES = [
  "Planned",
  "To Do",
  "R$D",
  "In-Progress",
  "Blocked",
  "Dev QA Done",
  "Issues",
  "QA Done",
  "Completed",
];

export const normalizeListName = (name) => String(name ?? "").trim().toLowerCase();

export const isPlannedList = (list) => normalizeListName(list?.name) === "planned";

export const isToDoList = (list) => normalizeListName(list?.name) === "to do";

export const isInProgressList = (list) => {
  // Accept "In-Progress", "In Progress", "InProgress", etc.
  const key = normalizeListName(list?.name).replace(/[^a-z0-9]/g, "");
  return key === "inprogress";
};

export const isDevQaDoneList = (list) => normalizeListName(list?.name) === "dev qa done";

export const isCompletedList = (list) => {
  const normalized = normalizeListName(list?.name);
  return normalized === "completed" || normalized === "done";
};

export const isDefaultBoardList = (list) => {
  if (!list) return false;
  if (list.isDefault === true) return true;
  if (list.isDefault === false) return false;
  return DEFAULT_BOARD_LIST_NAMES.some(
    (name) => normalizeListName(name) === normalizeListName(list.name)
  );
};

export const isCustomAdminList = (list) => Boolean(list) && !isDefaultBoardList(list);

export const requiresEstimatedHoursForMove = (sourceList, destList) => {
  if (!destList || !sourceList) return false;
  if (String(sourceList.listId) === String(destList.listId)) return false;
  if (isPlannedList(sourceList) && isToDoList(destList)) return false;
  if (isToDoList(sourceList)) return true;
  if (isPlannedList(destList)) return false;
  if (isCustomAdminList(destList)) return false;
  return true;
};

export const missingEstimatedHoursForMove = (sourceList, destList, card) => {
  if (hasEstimatedHours(card)) return false;
  return requiresEstimatedHoursForMove(sourceList, destList);
};

export const hasEstimatedHours = (card) => {
  const value = card?.estimatedHours;
  if (value == null || value === "") return false;
  const parsed = Number(value);
  return !Number.isNaN(parsed) && parsed >= 0;
};

export const isEmployeeBoardUser = (user) =>
  Number(user?.roleId) === EMPLOYEE_ROLE_ID;

export const requiresActualHoursForMove = (destList, sourceList) => {
  if (!destList || !sourceList) return false;
  if (String(sourceList.listId) === String(destList.listId)) return false;
  if (isPlannedList(sourceList)) return false;
  return isDevQaDoneList(destList);
};

/** Once work starts, cards cannot go back to To Do or Planned. */
export const isMoveFromInProgressToBacklog = (sourceList, destList) => {
  if (!sourceList || !destList) return false;
  if (String(sourceList.listId) === String(destList.listId)) return false;
  if (!isInProgressList(sourceList)) return false;
  return isToDoList(destList) || isPlannedList(destList);
};

export const IN_PROGRESS_BACKLOG_MOVE_MESSAGE =
  "Cards in In-Progress cannot be moved back to To Do or Planned.";

export const validateCardMove = ({ sourceList, destList }) => {
  if (isMoveFromInProgressToBacklog(sourceList, destList)) {
    return { ok: false, message: IN_PROGRESS_BACKLOG_MOVE_MESSAGE };
  }
  return { ok: true };
};

export const validateEmployeeCardMove = ({
  user,
  card,
  sourceList,
  destList,
}) => {
  const backlogRule = validateCardMove({ sourceList, destList });
  if (!backlogRule.ok) return backlogRule;

  if (!isEmployeeBoardUser(user)) {
    return { ok: true };
  }

  if (isCompletedList(sourceList) || isCompletedList(destList)) {
    return { ok: false, message: "You cannot move cards in Completed." };
  }

  const isCrossListMove = String(sourceList?.listId) !== String(destList?.listId);
  if (isCrossListMove && missingEstimatedHoursForMove(sourceList, destList, card)) {
    return {
      ok: false,
      needsEstimatedHours: true,
      message: "Please add estimated hours before moving this card.",
    };
  }

  return { ok: true };
};
